/**
 * Custom hooks for dashboard data management
 */

import { useState, useEffect, useCallback } from 'react';
import dashboardService from '../services/dashboardService';

/**
 * Hook for dashboard overview data
 */
export const useDashboard = () => {
    const [dashboard, setDashboard] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const fetchDashboard = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const data = await dashboardService.getDashboardOverview();
            setDashboard(data.data);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchDashboard();
    }, [fetchDashboard]);

    return { dashboard, loading, error, refetch: fetchDashboard };
};

/**
 * Hook for saved properties management
 */
export const useSavedProperties = () => {
    const [properties, setProperties] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const fetchProperties = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const data = await dashboardService.getSavedProperties();
            setProperties(data);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, []);

    const saveProperty = useCallback(async (propertyData) => {
        try {
            await dashboardService.saveProperty(propertyData);
            await fetchProperties(); // Refresh list
            return { success: true };
        } catch (err) {
            return { success: false, error: err.message };
        }
    }, [fetchProperties]);

    const unsaveProperty = useCallback(async (propertyId) => {
        try {
            // Optimistic update
            setProperties(prev => prev.filter(p => p.propertyId !== propertyId));
            await dashboardService.unsaveProperty(propertyId);
            return { success: true };
        } catch (err) {
            // Rollback on error
            await fetchProperties();
            return { success: false, error: err.message };
        }
    }, [fetchProperties]);

    useEffect(() => {
        fetchProperties();
    }, [fetchProperties]);

    return {
        properties,
        loading,
        error,
        saveProperty,
        unsaveProperty,
        refetch: fetchProperties
    };
};

/**
 * Hook for application history
 */
export const useApplications = (statusFilter = null) => {
    const [applications, setApplications] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const fetchApplications = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const data = await dashboardService.getApplications(statusFilter);
            setApplications(data);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [statusFilter]);

    useEffect(() => {
        fetchApplications();
    }, [fetchApplications]);

    return { applications, loading, error, refetch: fetchApplications };
};

/**
 * Hook for messages management
 */
export const useMessages = (messageType = 'received') => {
    const [messages, setMessages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [sending, setSending] = useState(false);

    const fetchMessages = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const data = await dashboardService.getMessages(messageType);
            setMessages(data);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [messageType]);

    const sendMessage = useCallback(async (messageData) => {
        try {
            setSending(true);
            await dashboardService.sendMessage(messageData);
            await fetchMessages(); // Refresh messages
            return { success: true };
        } catch (err) {
            return { success: false, error: err.message };
        } finally {
            setSending(false);
        }
    }, [fetchMessages]);

    useEffect(() => {
        fetchMessages();
    }, [fetchMessages]);

    return {
        messages,
        loading,
        error,
        sending,
        sendMessage,
        refetch: fetchMessages
    };
};

/**
 * Hook for user settings
 */
export const useSettings = () => {
    const [settings, setSettings] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [updating, setUpdating] = useState(false);

    const fetchSettings = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const data = await dashboardService.getSettings();
            setSettings(data);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, []);

    const updateSettings = useCallback(async (updates) => {
        try {
            setUpdating(true);
            await dashboardService.updateSettings(updates);
            await fetchSettings(); // Refresh settings
            return { success: true };
        } catch (err) {
            return { success: false, error: err.message };
        } finally {
            setUpdating(false);
        }
    }, [fetchSettings]);

    useEffect(() => {
        fetchSettings();
    }, [fetchSettings]);

    return {
        settings,
        loading,
        error,
        updating,
        updateSettings,
        refetch: fetchSettings
    };
};
